<template>
  <CapacityDatabasePage
    :title="title"
    :hero="hero"
    :tabs="tabs"
    :is-authorized="isAuthorized"
  >
    <NuxtPage @set-hero-ctas="setHeroCTAs" />
  </CapacityDatabasePage>
</template>

<script setup lang="ts">
/** components */
import CapacityDatabasePage from '~/components/capacity-database/CapacityDatabasePage.vue'

/** constants */
import { CATHODE, PRECURSOR_ASSESSMENT } from '~/constants/services/constants'

/** types */
import { useNuxtApp } from 'nuxt/app'
import { getServices } from '~/constants/services'
import type { THeroDeprecated } from '~/src/types/common'
import type { TFileDownloadInfo } from '~/src/types/download'

/** composables */
import { useAuthorization } from '~/composables/useAuthorization'

/** stores */
import { CAPACITY_DATABASE_SUBSCRIPTIONS } from '~/src/constants/capacity-database/common'
import useAuthStore from '~/stores/auth'

/** app */
definePageMeta({
  layout: 'services',
})
const { $modal } = useNuxtApp()
const { hasAuthorized } = useAuthorization()
const authStore = useAuthStore()

/** refs */
const isAuthorized = ref(false)
const title = ref({
  icon: CATHODE.iconColor,
  text: `${CATHODE.name} & ${PRECURSOR_ASSESSMENT.capacityName} Capacity Database`,
})
const hero = ref<THeroDeprecated>({
  initialFields: [],
  title: title.value.text,
  images: {
    desktop:
      'https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,w_1920,f_auto/v1719502633/assets/market/cathode/prices/benchmarkminerals_Graphs_and_charts_photorealistic_realistic_sh_b618f3c7-6778-4a79-8040-ab977633ec26_zudvju.png?_i=AA',
    mobile:
      'https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,w_1024,f_auto/v1719502633/assets/market/cathode/prices/benchmarkminerals_Graphs_and_charts_photorealistic_realistic_sh_b618f3c7-6778-4a79-8040-ab977633ec26_zudvju.png?_i=AA',
  },
  file: {
    category: '',
    file_name: '',
    EnvKey: '',
    ID: '',
  },
})

const tabs = computed(() => {
  const allTabs = [
    {
      name: 'Cathode',
      slug: CATHODE.slug,
      url: `/${CATHODE.slug}/capacity-database/${CATHODE.slug}`,
    },
    {
      name: 'Precursor',
      slug: PRECURSOR_ASSESSMENT.capacitySlug,
      url: `/${CATHODE.slug}/capacity-database/${PRECURSOR_ASSESSMENT.capacitySlug}`,
    },
  ]
  if (!isAuthenticated.value) return allTabs
  return [
    ...allTabs,
    {
      name: 'Sodium ion Cathode',
      slug: 'sodium-ion-cathode',
      url: `/${CATHODE.slug}/capacity-database/sodium-ion-cathode`,
    },
  ]
})

const subscription = ref(CAPACITY_DATABASE_SUBSCRIPTIONS[CATHODE.slug])

/** computed */
const isAuthenticated = computed(() => authStore.authenticated)

/** methods */
const setHeroCTAs = (methodology: TFileDownloadInfo, checkboxes: string[]) => {
  hero.value.initialFields = checkboxes
  hero.value.file = methodology
}

/** lifecycle */
onMounted(async () => {
  const initialValues = getServices(hero.value.initialFields)
  $modal.setInitialFields(initialValues)
  if (isAuthenticated.value) {
    isAuthorized.value = await hasAuthorized(subscription.value)
  }
})
</script>
